import { Button } from '@design-system';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { DialogFlowWithStripe } from '../../../components/Containers/Dialog/DialogFlowWithStripe';
import { getProductRef } from '../../../helpers/purchasing';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuthenticate } from '../../../hooks/useAuthenticate';
import { useAuth, useFlow } from '../../../providers';
import { usePreAuthOfferCtx } from '../../../providers/PreAuthOfferProvider';
import { usePurchase } from '../../../providers/PurchaseProvider';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { AuthEntryPoint } from '../../auth/types';
import { useOfferFlow } from '../useOfferFlow';

type Props = {
  product: ProductType;
};

export const PreAuthSoldOutOfferButton = ({ product }: Props) => {
  const { isLoggedIn } = useAuth();
  const { startFlow } = useFlow();
  const { t } = useTranslation(['product', 'authentication']);
  const purchasingController = usePurchase();
  const productRef = getProductRef(purchasingController);
  const { authenticateAndSubscribe } = useAuthenticate();
  const {
    data,
    handleConfirmOffer,
    handleSetupPreAuthFlow,
    loading,
    setupSecret,
  } = usePreAuthOfferCtx();
  const flowConfig = useOfferFlow(product);
  const resolvedArtist = product.productArtist?.[0] as ResolvedRelatedArtist;
  const router = useRouter();

  const subscriptions = buildSubscriptions({
    'artists': product.productArtist?.map(
      (a: ResolvedRelatedArtist) => a.artist,
    ),
    'isPreOrder': false,
    'productId': product.id[0].id,
    'productRef': productRef,
    'productTitle': product.title,
  });

  const { isSubscribedAll, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const start = useCallback(async () => {
    if (!setupSecret) {
      await handleSetupPreAuthFlow();
    }
    startFlow(flowConfig);
  }, [handleSetupPreAuthFlow, setupSecret, startFlow, flowConfig]);

  const enterOfferFlow = useCallback(() => {
    if (isLoggedIn && isSubscribedAll) {
      start();
      return;
    }
    authenticateAndSubscribe({
      authScreenThumbnail: product.thumbnail.filename,
      authScreenTitle: t('authentication:dynamicTitle.offer'),
      analyticsProperties: {
        'artist': resolvedArtist.artist.name,
        'artist name': resolvedArtist.artist.name,
        'button_location': 'product page',
        'product_id': product.id[0].id,
        'product_status': 'coming soon',
        'product_title': product.title,
        'source': AuthEntryPoint.AuctionEntry,
      },
      hideSuccessScreen: true,
      onFlowComplete: () => {
        setTimeout(() => {
          start();
          refetch();
        }, 150);
      },
      optInGeneral: false,
      subscriptions,
    });

    if (isLoggedIn) {
      start();
    }
  }, [
    authenticateAndSubscribe,
    isLoggedIn,
    isSubscribedAll,
    product.thumbnail.filename,
    product.title,
    refetch,
    resolvedArtist.artist.name,
    subscriptions,
    t,
    start,
    product.id,
  ]);

  useEffect(() => {
    const setupIntent = router.query.setup_intent as string;
    const setupIntentClientSecret = router.query
      .setup_intent_client_secret as string;

    if (setupIntent && setupIntentClientSecret && isLoggedIn) {
      handleConfirmOffer();
    }
  }, [router.query, isLoggedIn, handleConfirmOffer]);

  if (isLoggedIn && data?.confirmedAt != null) {
    return (
      <Button
        disabled
        fullWidth
        icon="ic_check"
        label={t('subscriptionCta.soldOut.offers.buttonSuccessTitle')}
        size="lg"
        variant="positive"
      />
    );
  }

  return (
    <>
      <Button
        className="text-center"
        disabled={loading}
        label={t('subscriptionCta.soldOut.offers.buttonTitle')}
        loading={loading}
        onClick={enterOfferFlow}
      />
      {setupSecret && <DialogFlowWithStripe clientSecret={setupSecret} />}
    </>
  );
};
